export type DecorCategory =
  | 'FLORALS'
  | 'LIGHTING'
  | 'RECEPTION - COCKTAIL HOUR'
  | 'SPECIAL ELEMENTS'
  | 'WEDDING COLORS'
  | 'TRADITIONAL MARRIAGE'
  | 'CHURCH'
  | 'OTHER';

export type DecorStatus = 'planned' | 'confirmed' | 'procured' | 'installed';

export interface DecorItem {
  id: string;
  category: DecorCategory;
  area: string;
  decor_element: string;
  color_scheme: string;
  /** Planned/target quantity needed — distinct from `stock`, the quantity
   *  actually procured and on hand. */
  quantity: number | null;
  vendor_name: string;
  notes: string;
  status: DecorStatus;
  /** IDs of events this item belongs to (multi-event) */
  event_ids?: string[];
  /** Specific area within the venue, e.g. "Entrance Arch", "Main Stage" */
  venue_area?: string;
  /** Raw array of image URLs stored in DB as JSONB */
  reference_images?: string[];
  /** Canva embed URLs stored in DB as JSONB */
  canva_embeds?: string[];
  /** @deprecated Superseded by procurement_item_allocations.decor_item_id —
   *  see ANS-1. Kept on historical rows only; the form no longer writes it. */
  source_product_id?: string | null;
  /** @deprecated Superseded by procurement_item_allocations.decor_item_id —
   *  see ANS-1. Kept on historical rows only; the form no longer writes it. */
  source_budget_item_id?: string | null;
  /** @deprecated Superseded by procurement_item_allocations.decor_item_id —
   *  see ANS-1. Kept on historical rows only; the form no longer writes it. */
  procurement_order_id?: string | null;
  /** Units actually procured and on hand — resolved from
   *  procurement_item_allocations.decor_item_id, incremented on delivery by
   *  ProcurementOrderEdit's goesTo('decor') branch. This is the canonical
   *  "where did this stock come from" mechanism (ANS-1), replacing the three
   *  deprecated fields above. */
  stock: number;
  /** Convenience alias: reference_images[0] */
  imageUrl?: string;
  archived?: boolean;
  created_at: string;
  updated_at: string;
}

export const DECOR_CATEGORY_ORDER: DecorCategory[] = [
  'FLORALS',
  'LIGHTING',
  'RECEPTION - COCKTAIL HOUR',
  'SPECIAL ELEMENTS',
  'TRADITIONAL MARRIAGE',
  'CHURCH',
  'WEDDING COLORS',
  'OTHER',
];

export const DECOR_CATEGORY_COLORS: Record<DecorCategory, string> = {
  'FLORALS': '#c19660',
  'LIGHTING': '#5b7a9c',
  'RECEPTION - COCKTAIL HOUR': '#7a5b9c',
  'SPECIAL ELEMENTS': '#9c5b7a',
  'TRADITIONAL MARRIAGE': '#8b5a3c',
  'CHURCH': '#5b9c8a',
  'WEDDING COLORS': '#d4a574',
  'OTHER': '#a09080',
};

// The enum values themselves stay ALL-CAPS (persisted as-is), but every
// other select in this form/area renders Title Case options — this is the
// display-only mapping so the Category dropdown matches.
export const DECOR_CATEGORY_LABELS: Record<DecorCategory, string> = {
  'FLORALS': 'Florals',
  'LIGHTING': 'Lighting',
  'RECEPTION - COCKTAIL HOUR': 'Reception - Cocktail Hour',
  'SPECIAL ELEMENTS': 'Special Elements',
  'TRADITIONAL MARRIAGE': 'Traditional Marriage',
  'CHURCH': 'Church',
  'WEDDING COLORS': 'Theme Colors',
  'OTHER': 'Other',
};

export const DECOR_STATUS_LABELS: Record<DecorStatus, string> = {
  planned: 'Planned',
  confirmed: 'Confirmed',
  procured: 'Procured',
  installed: 'Installed',
};

export const DECOR_STATUS_ORDER: DecorStatus[] = ['planned', 'confirmed', 'procured', 'installed'];

/** A single physical placement of a decor item on a floor plan — one item
 * can have multiple placements up to its quantity. */
export interface DecorPlacement {
  id: number;
  decor_id: number;
  floor_plan_id: number;
  x: number;
  y: number;
  created_at: string;
  updated_at: string;
}
